import React, { useMemo, useCallback } from 'react';
import { Icon } from '../Icon';
import { PageNumbers } from './PageNumbers';
import { PageInput } from './PageInput';
import { cn } from '@workspace/lib';
import { theme } from '../../theme';

export interface NavigationControlsProps {
  currentPage: number;
  totalPages: number;
  variant: 'jumper' | 'default';
  onPageChange: (page: number) => void;
  onScrollToTop?: () => void;
  classNames?: {
    container?: string;
    pageNumbers?: string;
  };
  hideOnSinglePage?: boolean;
}

export function NavigationControls({
  currentPage,
  totalPages,
  variant,
  onPageChange,
  onScrollToTop,
  classNames,
  hideOnSinglePage,
}: NavigationControlsProps) {
  const canGoPrevious = useMemo(() => currentPage > 1, [currentPage]);
  const canGoNext = useMemo(() => currentPage < totalPages, [currentPage, totalPages]);

  const handlePreviousPage = useCallback(() => {
    if (canGoPrevious) {
      onPageChange(currentPage - 1);
      onScrollToTop?.();
    }
  }, [canGoPrevious, currentPage, onPageChange, onScrollToTop]);

  const handleNextPage = useCallback(() => {
    if (canGoNext) {
      onPageChange(currentPage + 1);
      onScrollToTop?.();
    }
  }, [canGoNext, currentPage, onPageChange, onScrollToTop]);

  const handlePageClick = useCallback(
    (page: number) => {
      onPageChange(page);
      onScrollToTop?.();
    },
    [onPageChange, onScrollToTop],
  );

  if (totalPages === 0 || (hideOnSinglePage && totalPages === 1)) {
    return null;
  }

  return (
    <div className={cn('flex items-center', classNames?.container)}>
      {/* The button tag will be changed to Button component later in another ticket */}
      <button
        onClick={handlePreviousPage}
        disabled={!canGoPrevious}
        className={`flex items-center justify-center w-11 h-11 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:ring-offset-2 ${canGoPrevious ? `text-primary ${theme === 'redRec' ? 'hover:text-light-green' : 'hover:text-primary-500'} cursor-pointer` : 'text-secondary-300 cursor-not-allowed'}`}
        style={{ borderRadius: '4px' }}
      >
        <Icon name="ChevronLeft" size={24} />
      </button>

      {variant === 'jumper' ? (
        <PageNumbers
          className={classNames?.pageNumbers}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageClick={handlePageClick}
        />
      ) : (
        <PageInput
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={onPageChange}
          onScrollToTop={onScrollToTop}
        />
      )}

      <button
        onClick={handleNextPage}
        disabled={!canGoNext}
        className={`flex items-center justify-center w-11 h-11 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary focus:ring-offset-2 ${canGoNext ? `text-primary ${theme === 'redRec' ? 'hover:text-light-green' : 'hover:text-primary-500'} cursor-pointer` : '!text-secondary-300 cursor-not-allowed'}`}
        style={{ borderRadius: '4px' }}
      >
        <Icon name="ChevronRight" size={24} />
      </button>
    </div>
  );
}
